/**
 * Road routing.
 *
 * Backed by OSRM's public demo server, which is the one routing service that
 * needs no account and no key — the app has no backend to hold a secret in, so
 * anything requiring a token could not be shipped here at all.
 *
 * WHAT THIS IS NOT
 * The demo server is a courtesy, not an SLA: it is rate limited, it can be
 * slow, and it can be down. Every call therefore has a timeout and a clean
 * failure, and the panel falls back to the straight line rather than showing
 * nothing. A deployment that depends on routing should point BASE_URL at its
 * own OSRM instance, or at a keyed provider.
 *
 * ONLY DRIVING is offered, and that is deliberate. Asking the demo server for
 * `walking` or `cycling` returns a route — but checked against the same two
 * points, all three profiles answer with the identical distance, duration and
 * step list, because only the driving profile is actually loaded. Offering a
 * travel-mode selector that silently returns driving numbers would be worse
 * than offering one mode honestly.
 */

const BASE_URL = 'https://router.project-osrm.org/route/v1/driving';

/** How long to wait before deciding the demo server is not going to answer. */
const TIMEOUT_MS = 12000;

export interface RouteStep {
  /** "Turn right onto Old Delhi Gurgaon Road" — assembled, not raw. */
  instruction: string;
  /** Metres travelled on this step. */
  metres: number;
}

export interface RouteResult {
  metres: number;
  seconds: number;
  /** [lng, lat] positions along the road, for drawing. */
  coordinates: [number, number][];
  steps: RouteStep[];
}

/** OSRM's maneuver vocabulary, written the way a person would say it. */
function describe(step: any): string {
  const maneuver = step?.maneuver ?? {};
  const type = String(maneuver.type ?? '');
  const modifier = String(maneuver.modifier ?? '');
  const road = String(step?.name ?? '').trim();
  const onto = road ? ` onto ${road}` : '';

  switch (type) {
    case 'depart':
      return road ? `Head out on ${road}` : 'Head out';
    case 'arrive':
      return 'Arrive at your destination';
    case 'roundabout':
    case 'rotary':
      return maneuver.exit ? `Take exit ${maneuver.exit} at the roundabout${onto}` : `Take the roundabout${onto}`;
    case 'merge':
      return `Merge${modifier ? ` ${modifier}` : ''}${onto}`;
    case 'on ramp':
      return `Take the ramp${onto}`;
    case 'off ramp':
      return `Take the exit${onto}`;
    case 'fork':
      return `Keep ${modifier || 'ahead'}${onto}`;
    case 'end of road':
      return `At the end of the road, turn ${modifier || 'ahead'}${onto}`;
    case 'continue':
      /* OSRM folds a U-turn into `continue`, and "Continue uturn onto Old
         Delhi Gurgaon Road" is not an instruction anyone would give aloud.
         The other modifiers mean "keep to this side", not "turn". */
      if (modifier === 'uturn') return `Make a U-turn${onto}`;
      if (modifier && modifier !== 'straight') return `Keep ${modifier}${onto}`;
      return `Continue${onto}`;
    case 'new name':
      return `Continue${onto}`;
    case 'turn':
      if (modifier === 'uturn') return `Make a U-turn${onto}`;
      return `Turn ${modifier || 'ahead'}${onto}`;
    default:
      return `${type || 'Continue'}${modifier ? ` ${modifier}` : ''}${onto}`.trim();
  }
}

export class RoutingError extends Error {}

/**
 * A driving route between two points.
 *
 * Throws `RoutingError` with something worth reading — the caller shows it and
 * falls back to the straight line, so "the router is down" must not look the
 * same as "there is no road between these places".
 */
export async function routeBetween(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): Promise<RouteResult> {
  const path = `${from.lng},${from.lat};${to.lng},${to.lat}`;
  const url = `${BASE_URL}/${path}?overview=full&geometries=geojson&steps=true`;

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);

  let payload: any;
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new RoutingError(`The routing service answered ${response.status}.`);
    payload = await response.json();
  } catch (error) {
    if (error instanceof RoutingError) throw error;
    throw new RoutingError(
      (error as Error)?.name === 'AbortError'
        ? 'The routing service did not answer in time.'
        : 'The routing service could not be reached.',
    );
  } finally {
    window.clearTimeout(timer);
  }

  /* OSRM reports failures as HTTP 200 with a code in the body — the same shape
     of trap ArcGIS has, and worth checking for the same reason. */
  if (payload?.code !== 'Ok') {
    throw new RoutingError(
      payload?.code === 'NoRoute'
        ? 'No road route connects those two places.'
        : payload?.message || 'The routing service could not build a route.',
    );
  }

  const route = payload.routes?.[0];
  if (!route) throw new RoutingError('The routing service returned no route.');

  const steps: RouteStep[] = (route.legs ?? []).flatMap((leg: any) =>
    (leg.steps ?? []).map((step: any) => ({
      instruction: describe(step),
      metres: Number(step?.distance ?? 0),
    })),
  );

  return {
    metres: Number(route.distance ?? 0),
    seconds: Number(route.duration ?? 0),
    coordinates: (route.geometry?.coordinates ?? []) as [number, number][],
    steps,
  };
}

/** "18 min", "1 hr 05 min" — a duration read the way a person says it. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  return `${hours} hr ${String(total % 60).padStart(2, '0')} min`;
}
